import React, { useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import { X } from 'lucide-react';
import { MnemonicScope } from '@/contexts/MnemonicContext';
import { SCOPE_DIALOG } from '@/lib/mnemonics/registry';

/**
 * The replacement for the edit dialog.
 *
 * A modal takes the whole screen away to collect two fields, and takes with it
 * the list you were reading when you decided to make the edit. That is fine for
 * "are you sure you want to delete this" and wrong for everything else — which
 * is most of the fifty dialogs this app had.
 *
 * A SidePane docks to the right and leaves the page behind it readable and
 * scrollable. You can compare while you edit, which is the thing a modal makes
 * impossible. Destructive confirmations stay modal on purpose: those *should*
 * interrupt.
 */
export function SidePane({
  open,
  onClose,
  title,
  subtitle,
  footer,
  children,
  width = '34rem',
  overlay = false,
  className,
  'data-testid': testId,
}) {
  const panelRef = useRef(null);
  const restoreTo = useRef(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => { onCloseRef.current = onClose; });

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onCloseRef.current?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  // keyed on `open` alone: callers pass inline onClose arrows, so depending on
  // one re-ran this on every keystroke and yanked focus out of the field
  useEffect(() => {
    if (!open) return undefined;

    restoreTo.current = document.activeElement;

    // querySelector answers in document order, not selector order, so the header
    // close button beats the form unless the typable fields are asked for first
    const panel = panelRef.current;
    const focusTarget =
      panel?.querySelector('input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled])') ||
      panel?.querySelector('button:not([disabled]), [tabindex]:not([tabindex="-1"])');
    focusTarget?.focus();

    return () => {
      if (restoreTo.current instanceof HTMLElement) restoreTo.current.focus();
    };
  }, [open]);

  if (!open) return null;

  return (
    <>
      {overlay && (
        <div
          className="fixed inset-0 z-40 bg-slate-900/20 lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}
      <aside
        ref={panelRef}
        role="dialog"
        aria-modal="false"
        aria-label={typeof title === 'string' ? title : 'Details'}
        data-testid={testId || 'side-pane'}
        style={{ '--pane-w': width }}
        className={cn(
          'fixed inset-y-0 right-0 z-40 flex w-full flex-col border-l border-slate-200 bg-white shadow-2xl',
          'lg:w-[var(--pane-w)]',
          className
        )}
      >
        <header className="flex shrink-0 items-start justify-between gap-3 border-b border-slate-200 bg-slate-50 px-3 py-2">
          <div className="min-w-0">
            <h2 className="truncate font-heading text-sm font-semibold text-slate-900">{title}</h2>
            {subtitle && <p className="truncate text-xs text-slate-500">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="-mr-1 shrink-0 rounded p-1 text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-700"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        {/* An open pane owns the mnemonics even though it is not modal: focus is
            inside it, so Alt+S must mean the pane's Save and not a page action
            hidden behind it. Page underlines suppress themselves while it is
            open, so nothing on screen claims a key it no longer owns. */}
        <MnemonicScope type={SCOPE_DIALOG} label="side-pane">
          <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>

          {footer && (
            <footer className="flex shrink-0 items-center justify-end gap-2 border-t border-slate-200 bg-slate-50 px-3 py-2">
              {footer}
            </footer>
          )}
        </MnemonicScope>
      </aside>
    </>
  );
}

export default SidePane;
